import { h, type Component, type VNodeChild } from 'vue';
import ItpBadge from '../../badge/ItpBadge.vue';
import { compilePath } from '../gridCell';
import type { FormatterFactory } from '../types';

export interface BadgeOptions {
  variant?: string | null;
  variantMap?: Record<string, string> | null;
  variantMapField?: string | null;
  colorCode?: string | null;
  block?: boolean;
  noBoxShadow?: boolean;
  pill?: boolean;
  dot?: boolean;
  badgeClass?: string | ((row: any, value: any) => string);
}

function dotIcon(): VNodeChild {
  return h('span', { class: 'pe-1' }, [
    h(
      'svg',
      {
        xmlns: 'http://www.w3.org/2000/svg',
        width: '1em',
        height: '1em',
        viewBox: '0 0 16 16',
      },
      [h('circle', { cx: '8', cy: '8', r: '4', fill: 'currentColor' })],
    ),
  ]);
}

export const badge: FormatterFactory = (options: BadgeOptions = {}) => {
  const { pill, dot, badgeClass, variantMapField, ...badgeProps } = options;

  const classFor = (value: any, row: any) =>
    [
      pill ? 'rounded-pill' : null,
      dot ? 'badge-dot' : null,
      typeof badgeClass === 'function' ? badgeClass(row, value) : badgeClass,
    ].filter(Boolean);

  const readMapKey = variantMapField ? compilePath(variantMapField) : null;
  const valueFor = (value: any, row: any) =>
    readMapKey ? readMapKey(row) : value;

  if (!dot) {
    return {
      component: ItpBadge,
      props: (value, row) => ({
        ...badgeProps,
        value: valueFor(value, row),
        class: classFor(value, row),
      }),
    };
  }

  const renderer = (
    props: { value: any; context: any },
    { slots }: { slots: Record<string, undefined | (() => VNodeChild)> },
  ): VNodeChild =>
    h(
      ItpBadge,
      {
        ...badgeProps,
        value: valueFor(props.value, props.context),
        class: classFor(props.value, props.context),
      },
      () => [dotIcon(), slots.default?.()],
    );

  renderer.props = ['value', 'context'];

  return {
    component: renderer as Component,
    props: (_value, row) => ({ context: row }),
  };
};
